import { createFileRoute } from '@tanstack/react-router'
import { useAuth } from '@/hooks/use-auth'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'

function DebugPage() {
  const { user, isAuthenticated, isLoading, checkSession, clearError } =
    useAuth()

  return (
    <div className="container mx-auto py-8 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Debug Information</h1>
        <div className="flex gap-2">
          <Button onClick={checkSession} variant="outline" size="sm">
            Check Session
          </Button>
          <Button onClick={clearError} variant="outline" size="sm">
            Clear Error
          </Button>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Authentication Status</CardTitle>
            <CardDescription>Current authentication state</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Status:</span>
              <Badge variant={isAuthenticated ? 'default' : 'destructive'}>
                {isAuthenticated ? 'Authenticated' : 'Not Authenticated'}
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Loading:</span>
              <Badge variant={isLoading ? 'secondary' : 'outline'}>
                {isLoading ? 'Loading' : 'Idle'}
              </Badge>
            </div>
            <div className="space-y-2">
              <span className="text-sm font-medium">Tokens:</span>
              <div className="text-xs text-muted-foreground space-y-1">
                <div>
                  Access Token:{' '}
                  {localStorage.getItem('access_token')
                    ? '✓ Present'
                    : '✗ Missing'}
                </div>
                <div>
                  Refresh Token:{' '}
                  {localStorage.getItem('refresh_token')
                    ? '✓ Present'
                    : '✗ Missing'}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>User Information</CardTitle>
            <CardDescription>Current user data</CardDescription>
          </CardHeader>
          <CardContent>
            {user ? (
              <div className="space-y-2 text-sm">
                <div>
                  <span className="font-medium">ID:</span> {user.id}
                </div>
                <div>
                  <span className="font-medium">Email:</span> {user.email}
                </div>
                <div>
                  <span className="font-medium">Username:</span> {user.username}
                </div>
                <div>
                  <span className="font-medium">Name:</span> {user.fullName}
                </div>
                <div>
                  <span className="font-medium">Verified:</span>{' '}
                  {user.emailVerified ? 'Yes' : 'No'}
                </div>
                <div>
                  <span className="font-medium">Active:</span>{' '}
                  {user.isActive ? 'Yes' : 'No'}
                </div>
                <div>
                  <span className="font-medium">Login Count:</span>{' '}
                  {user.loginCount}
                </div>
                <div>
                  <span className="font-medium">Created:</span>{' '}
                  {new Date(user.createdAt).toLocaleString()}
                </div>
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">
                No user data available
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Environment</CardTitle>
            <CardDescription>Runtime environment information</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div>
              <span className="font-medium">Mode:</span> {import.meta.env.MODE}
            </div>
            <div>
              <span className="font-medium">Dev:</span>{' '}
              {import.meta.env.DEV ? 'Yes' : 'No'}
            </div>
            <div>
              <span className="font-medium">API URL:</span>{' '}
              {import.meta.env.VITE_API_URL || 'Not set'}
            </div>
            <div>
              <span className="font-medium">MSW:</span>{' '}
              {import.meta.env.VITE_ENABLE_MSW || 'Not set'}
            </div>
            <div>
              <span className="font-medium">DevTools:</span>{' '}
              {import.meta.env.VITE_ENABLE_DEVTOOLS || 'Not set'}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Local Storage</CardTitle>
            <CardDescription>Browser storage contents</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-xs">
              {Object.entries(localStorage).map(([key, value]) => (
                <div key={key} className="border-b pb-1">
                  <div className="font-medium">{key}</div>
                  <div className="text-muted-foreground truncate">
                    {key.includes('token') ? '***' : value}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export const Route = createFileRoute('/debug/')({
  component: DebugPage,
})
